import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatErr, fileViewUrl } from "../lib/api";
import {
  ArrowLeft, MapPin, Calendar, Phone, Home, Droplets, Heart, Pencil, Trash2,
  ChevronDown, ChevronRight, Plus, History, ClipboardCheck, BookOpen, FileImage, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import AiReportPanel from "../components/AiReportPanel";
import MapEmbed from "../components/MapEmbed";
import CareProviderCard from "../components/CareProviderCard";
import AddRevisitModal from "../components/AddRevisitModal";
import AllotModal from "../components/AllotModal";
import { useAuth } from "../context/AuthContext";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function HouseholdDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const isFaculty = user?.role === "faculty" || user?.role === "manager";

  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revisits, setRevisits] = useState([]);
  const [revisitsLoading, setRevisitsLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [allotOpen, setAllotOpen] = useState(false);
  // Track which visit blocks are expanded (default: latest visit expanded)
  const [expanded, setExpanded] = useState(() => new Set());
  // Track which Year/Month accordions are open (defaults below once data arrives)
  const [openYears, setOpenYears] = useState(new Set());
  const [openMonths, setOpenMonths] = useState(new Set());

  const loadHousehold = () =>
    api.get(`/households/${id}`)
      .then((r) => setH(r.data))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));

  const loadRevisits = () => {
    setRevisitsLoading(true);
    return api.get(`/households/${id}/revisits`)
      .then((r) => setRevisits(r.data))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setRevisitsLoading(false));
  };

  useEffect(() => { loadHousehold(); loadRevisits(); /* eslint-disable-next-line */ }, [id]);

  // Combine original household visit + revisits into a single timeline
  const allVisits = useMemo(() => {
    if (!h) return [];
    const original = {
      id: `original-${h.id}`,
      kind: "original",
      visit_date: h.visit_date,
      observations: "",
      new_health_issues: h.health_issues || [],
      level_of_prevention: h.level_of_prevention,
      care_provided: h.care_provided,
      education_provided: h.education_provided,
      water_source: h.water_source,
      toilet_facility: h.toilet_facility,
      waste_disposal: h.waste_disposal,
      ventilation: h.ventilation,
      overall_hygiene: h.overall_hygiene,
      photo_paths: h.patient_photo_paths?.length ? h.patient_photo_paths : (h.patient_photo_path ? [h.patient_photo_path] : []),
      latitude: h.latitude,
      longitude: h.longitude,
      remarks: h.remarks,
      ai_report_id: h.ai_report_id,
      ai_report_text: h.ai_report_text,
      creator: h.creator || null,
      created_by: h.created_by,
      created_by_name: h.created_by_name,
    };
    const followups = (revisits || []).map((rv) => ({ ...rv, kind: "revisit" }));
    return [original, ...followups].sort((a, b) => (b.visit_date || "").localeCompare(a.visit_date || ""));
  }, [h, revisits]);

  // Group Year → Month → Visit
  const grouped = useMemo(() => {
    const map = new Map();
    for (const v of allVisits) {
      const d = (v.visit_date || "").slice(0, 10);
      const [y, m] = d ? d.split("-") : ["Unknown", "00"];
      const year = y || "Unknown";
      const month = m || "00";
      if (!map.has(year)) map.set(year, new Map());
      const months = map.get(year);
      if (!months.has(month)) months.set(month, []);
      months.get(month).push(v);
    }
    // Sort years desc, months desc, visits already sorted from allVisits
    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([year, months]) => [
        year,
        Array.from(months.entries()).sort((a, b) => b[0].localeCompare(a[0])),
      ]);
  }, [allVisits]);

  // Auto-expand the latest year, latest month, and latest visit once data is loaded
  const [autoExpanded, setAutoExpanded] = useState(false);
  useEffect(() => {
    if (autoExpanded) return;
    // Wait for both household + revisits to finish loading
    if (loading || revisitsLoading) return;
    if (allVisits.length === 0) { setAutoExpanded(true); return; }
    const latest = allVisits[0];
    const y = (latest.visit_date || "").slice(0, 4);
    const m = (latest.visit_date || "").slice(5, 7);
    setOpenYears(new Set([y]));
    setOpenMonths(new Set([`${y}-${m}`]));
    setExpanded(new Set([latest.id]));
    setAutoExpanded(true);
  }, [loading, revisitsLoading, allVisits, autoExpanded]);

  const toggleSet = (setter, current, key) => {
    const next = new Set(current);
    next.has(key) ? next.delete(key) : next.add(key);
    setter(next);
  };

  const deleteHousehold = async () => {
    if (!window.confirm(`Permanently delete ${h.head_of_family}'s household and ALL its revisits? This cannot be undone.`)) return;
    try {
      await api.delete(`/households/${id}`);
      toast.success("Household deleted");
      navigate("/households");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteRevisit = async (rv) => {
    if (!window.confirm("Delete this revisit? This cannot be undone.")) return;
    try {
      await api.delete(`/revisits/${rv.id}`);
      toast.success("Revisit deleted");
      loadRevisits();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading) return <div className="text-brand-muted">Loading…</div>;
  if (!h) return null;

  return (
    <div className="max-w-7xl mx-auto space-y-5 animate-fade-up" data-testid="household-detail-root">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/households"))}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-md border-2 border-brand-primary bg-white text-brand-primary font-bold text-sm shadow-sm hover:bg-brand-primary hover:text-white transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={3} /> Back
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => setAddOpen(true)} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white text-sm font-bold shadow-sm" data-testid="add-revisit-btn">
            <Plus className="h-4 w-4" /> Add Revisit
          </button>
          {isFaculty && (
            <button onClick={() => setAllotOpen(true)} className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-sm font-medium" data-testid="allot-btn">
              <Plus className="h-3.5 w-3.5" /> Allot to Students
            </button>
          )}
          {isManager && (
            <>
              <button onClick={() => navigate(`/households/${id}/edit`)} className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-sm font-medium" data-testid="edit-household-btn">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button onClick={deleteHousehold} className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-sm font-medium text-brand-danger" data-testid="delete-household-btn">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Header */}
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">{h.village}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">{h.head_of_family}&apos;s home</h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-brand-muted">
          <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" /> First visit {h.visit_date}</span>
          {h.phone && <span className="inline-flex items-center gap-1"><Phone className="h-4 w-4" /> {h.phone}</span>}
          {h.address && <span className="inline-flex items-center gap-1"><Home className="h-4 w-4" /> {h.address}</span>}
          {h.latitude && h.longitude && (
            <a target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${h.latitude},${h.longitude}`} className="inline-flex items-center gap-1 text-brand-primary">
              <MapPin className="h-4 w-4" /> {h.latitude.toFixed?.(4)}, {h.longitude.toFixed?.(4)}
            </a>
          )}
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-primary/10 text-brand-primary px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
            <History className="h-3 w-3" /> {allVisits.length} {allVisits.length === 1 ? "visit" : "visits"} on record
          </span>
        </div>
      </div>

      {/* 2-column layout: sticky sidebar + visits timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* === STICKY SIDEBAR === */}
        <aside className="lg:col-span-4 xl:col-span-4 space-y-4 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto scrollbar-thin pb-4">
          {/* Family profile */}
          <div className="rounded-xl border border-brand-border bg-white shadow-tactile p-5" data-testid="family-profile">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-bold">Family Profile</div>
              <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-brand-surfaceAlt text-brand-primary px-2 py-0.5 border border-brand-border">
                {h.family_members?.length || 0} members
              </span>
            </div>

            <div className="font-heading text-lg font-semibold">{h.head_of_family}</div>
            <div className="text-xs text-brand-muted mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
              <span>{h.village}</span>
              {h.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {h.phone}</span>}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              <MiniStat label="Members" value={h.family_members?.length || 0} />
              <MiniStat label="Patients" value={(h.family_members || []).filter((m) => m.is_patient).length} accent="#D78A76" />
              <MiniStat label="Hygiene" value={h.overall_hygiene || "—"} small />
            </div>

            {h.family_members?.length > 0 && (
              <div className="mt-4 space-y-2">
                {h.family_members.map((m, i) => (
                  <div key={i} className="rounded-md border border-brand-border bg-brand-surfaceAlt/60 p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-sm truncate">
                        {m.name}
                        {m.is_patient && (
                          <span className="ml-2 inline-flex items-center text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-accent/20 text-brand-accent font-bold">
                            <Heart className="h-2.5 w-2.5 mr-0.5" /> Patient
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-brand-muted whitespace-nowrap">{m.age}y · <span className="capitalize">{m.gender}</span></span>
                    </div>
                    {(m.health_status || m.occupation || m.education) && (
                      <div className="text-[11px] text-brand-muted mt-1 flex flex-wrap gap-x-2">
                        {m.health_status && <span><b>HS:</b> {m.health_status}</span>}
                        {m.occupation && <span><b>Occ:</b> {m.occupation}</span>}
                        {m.education && <span><b>Edu:</b> {m.education}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {(h.water_source || h.toilet_facility || h.waste_disposal || h.ventilation) && (
              <div className="mt-4 rounded-md border border-brand-border p-3 bg-brand-surfaceAlt/60">
                <div className="text-[10px] uppercase tracking-wider text-brand-muted font-bold flex items-center gap-1 mb-1.5">
                  <Droplets className="h-3 w-3" /> Home hygiene
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                  {h.water_source && <span><b className="text-brand-muted">Water:</b> {h.water_source}</span>}
                  {h.toilet_facility && <span><b className="text-brand-muted">Toilet:</b> {h.toilet_facility}</span>}
                  {h.waste_disposal && <span><b className="text-brand-muted">Waste:</b> {h.waste_disposal}</span>}
                  {h.ventilation && <span><b className="text-brand-muted">Ventilation:</b> {h.ventilation}</span>}
                </div>
              </div>
            )}

            {h.latitude && h.longitude && (
              <div className="mt-4">
                <MapEmbed latitude={h.latitude} longitude={h.longitude} label={`${h.head_of_family}'s home`} />
              </div>
            )}
          </div>

          {/* Care provider profile (original visit) */}
          <CareProviderCard
            creator={h.creator || { name: h.created_by_name, role: "student" }}
            title="Initial Care Provider"
            testId="initial-care-provider"
          />

          {/* Allotted students */}
          {((h.assignees || []).length > 0 || isFaculty) && (
            <div className="rounded-xl border border-brand-border bg-white shadow-tactile p-5" data-testid="allotted-students">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-bold">Allotted Students</div>
                {isFaculty && (
                  <button onClick={() => setAllotOpen(true)} className="text-[10px] uppercase tracking-wider font-bold text-brand-primary hover:underline" data-testid="manage-allot-link">
                    {((h.assignees || []).length) ? "Manage" : "Allot"}
                  </button>
                )}
              </div>
              {(h.assignees || []).length === 0 ? (
                <div className="text-xs text-brand-muted">No students allotted yet. {isFaculty ? "Click Allot to assign this family for ongoing care." : ""}</div>
              ) : (
                <div className="space-y-2">
                  {(h.assignees || []).map((s) => {
                    const ini = (s.name || "?").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]).join("").toUpperCase();
                    return (
                      <div key={s.id} className="flex items-center gap-3 rounded-md border border-brand-border bg-brand-surfaceAlt/60 p-2" data-testid={`allotted-${s.id}`}>
                        <span className="h-9 w-9 rounded-full grid place-items-center font-heading font-semibold text-xs bg-brand-accent/15 text-[#9C5240] shrink-0">{ini || "?"}</span>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">{s.name}</div>
                          <div className="text-[10px] text-brand-muted flex flex-wrap gap-x-2">
                            {s.degree && <span>{s.degree}</span>}
                            {s.batch && <span>Batch {s.batch}</span>}
                            {s.roll_number && <span>Roll {s.roll_number}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {h.assigned_by_name && (h.assignees || []).length > 0 && (
                <div className="mt-3 text-[10px] text-brand-muted">Allotted by {h.assigned_by_name}</div>
              )}
            </div>
          )}
        </aside>

        {/* === VISITS TIMELINE === */}
        <section className="lg:col-span-8 xl:col-span-8 space-y-4">
          <div className="rounded-xl border border-brand-border bg-white shadow-tactile p-5" data-testid="visits-timeline">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-lg bg-brand-primary/10 grid place-items-center">
                  <History className="h-4 w-4 text-brand-primary" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-lg">Visit History</div>
                  <div className="text-xs text-brand-muted">Year → Month → Visit. Records are preserved permanently.</div>
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-wider font-bold rounded-full bg-brand-surfaceAlt text-brand-primary px-2.5 py-1 border border-brand-border">
                {allVisits.length} {allVisits.length === 1 ? "visit" : "visits"}
              </span>
            </div>

            {revisitsLoading ? (
              <div className="text-sm text-brand-muted mt-4">Loading…</div>
            ) : (
              <div className="mt-5 space-y-3">
                {grouped.map(([year, months]) => {
                  const yearKey = year;
                  const yearOpen = openYears.has(yearKey);
                  const yearVisitCount = months.reduce((acc, [, v]) => acc + v.length, 0);
                  return (
                    <div key={year} className="rounded-lg border border-brand-border bg-white overflow-hidden" data-testid={`year-${year}`}>
                      <button
                        type="button"
                        onClick={() => toggleSet(setOpenYears, openYears, yearKey)}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gradient-to-r from-brand-primary/8 to-transparent hover:from-brand-primary/12 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          {yearOpen ? <ChevronDown className="h-4 w-4 text-brand-primary" /> : <ChevronRight className="h-4 w-4 text-brand-primary" />}
                          <span className="font-heading font-bold text-lg text-brand-primary">{year}</span>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">
                          {yearVisitCount} {yearVisitCount === 1 ? "visit" : "visits"}
                        </span>
                      </button>

                      {yearOpen && (
                        <div className="px-3 sm:px-4 pb-3 space-y-3 pt-3 border-t border-brand-border">
                          {months.map(([monthNum, monthVisits]) => {
                            const monthKey = `${year}-${monthNum}`;
                            const monthOpen = openMonths.has(monthKey);
                            const monthLabel = MONTH_NAMES[parseInt(monthNum, 10) - 1] || monthNum;
                            return (
                              <div key={monthKey} className="rounded-md border border-brand-border bg-brand-surfaceAlt/40 overflow-hidden" data-testid={`month-${monthKey}`}>
                                <button
                                  type="button"
                                  onClick={() => toggleSet(setOpenMonths, openMonths, monthKey)}
                                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-brand-surfaceAlt transition-colors"
                                >
                                  <div className="flex items-center gap-2">
                                    {monthOpen ? <ChevronDown className="h-3.5 w-3.5 text-brand-muted" /> : <ChevronRight className="h-3.5 w-3.5 text-brand-muted" />}
                                    <span className="font-heading font-semibold text-sm">{monthLabel}</span>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-brand-muted">{year}</span>
                                  </div>
                                  <span className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">
                                    {monthVisits.length} {monthVisits.length === 1 ? "visit" : "visits"}
                                  </span>
                                </button>

                                {monthOpen && (
                                  <div className="px-2 sm:px-3 pb-3 space-y-2 pt-1">
                                    {monthVisits.map((v) => (
                                      <VisitBlock
                                        key={v.id}
                                        visit={v}
                                        expanded={expanded.has(v.id)}
                                        onToggle={() => toggleSet(setExpanded, expanded, v.id)}
                                        canDelete={user && (user.role === "manager" || user.role === "faculty" || user.id === v.created_by) && v.kind === "revisit"}
                                        onDelete={() => deleteRevisit(v)}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {allVisits.length === 0 && (
                  <div className="text-sm text-brand-muted py-4 text-center">No visits recorded yet.</div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      {addOpen && (
        <AddRevisitModal household={h} onClose={() => setAddOpen(false)} onSaved={loadRevisits} />
      )}

      {allotOpen && (
        <AllotModal
          household={h}
          onClose={() => setAllotOpen(false)}
          onSaved={(updated) => setH(updated)}
        />
      )}
    </div>
  );
}

/* =================== VISIT BLOCK =================== */
function VisitBlock({ visit, expanded, onToggle, canDelete, onDelete }) {
  const isOriginal = visit.kind === "original";
  const dateLabel = visit.visit_date || "—";
  const creator = visit.creator || { name: visit.created_by_name, role: "student" };
  const hasPhotos = (visit.photo_paths || []).length > 0;
  const newIssues = isOriginal ? (visit.new_health_issues || []) : (visit.new_health_issues || []);

  return (
    <div className="rounded-lg border border-brand-border bg-white" data-testid={`visit-block-${visit.id}`}>
      {/* Header */}
      <button type="button" onClick={onToggle} className="w-full text-left p-3 flex items-start gap-3 hover:bg-brand-surfaceAlt/60 transition-colors rounded-lg">
        <div className={`shrink-0 h-9 w-9 rounded-full grid place-items-center ${isOriginal ? "bg-brand-primary text-white" : "bg-brand-accent text-white"}`}>
          <Calendar className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading font-semibold text-sm">{dateLabel}</span>
            {isOriginal && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-primary/12 text-brand-primary font-bold">
                <Sparkles className="h-2.5 w-2.5 inline mr-0.5" /> Initial visit
              </span>
            )}
            {!isOriginal && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-accent/15 text-[#9C5240] font-bold">Follow-up</span>
            )}
            {visit.follow_up_required && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-primary/8 text-brand-primary font-bold">
                <ClipboardCheck className="h-2.5 w-2.5" /> Follow-up scheduled
              </span>
            )}
          </div>
          <div className="text-xs text-brand-muted mt-0.5 truncate">
            by <span className="font-medium text-foreground">{creator?.name || "—"}</span>
            {creator?.designation && <span> · {creator.designation}</span>}
            {creator?.batch && <span> · Batch {creator.batch}</span>}
            {creator?.degree && <span> · {creator.degree}</span>}
          </div>
          {!expanded && (
            <div className="text-xs text-brand-muted mt-1 line-clamp-1">
              {visit.observations || visit.care_provided || (newIssues.length ? `Issues: ${newIssues.join(", ")}` : "Click to view details")}
            </div>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-brand-muted shrink-0 mt-1 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-brand-border p-4 space-y-4">
          {/* Care provider profile (compact) */}
          <CareProviderCard creator={creator} compact testId={`visit-care-provider-${visit.id}`} />

          {/* Observations */}
          {visit.observations && (
            <Block label="Observations">{visit.observations}</Block>
          )}

          {/* Issues */}
          {newIssues.length > 0 && (
            <Block label={isOriginal ? "Health Issues Identified" : "New Issues"}>
              <div className="flex flex-wrap gap-2">
                {newIssues.map((x, i) => (
                  <span key={i} className="px-2.5 py-1 rounded-full bg-brand-accent/15 text-[#9C5240] text-xs font-medium">{x}</span>
                ))}
              </div>
            </Block>
          )}

          {visit.level_of_prevention && (
            <Block label="Level of Prevention">
              <span className="inline-block px-2.5 py-1 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-semibold">{visit.level_of_prevention}</span>
            </Block>
          )}

          {/* Hygiene chips */}
          {(visit.water_source || visit.toilet_facility || visit.waste_disposal || visit.ventilation || visit.overall_hygiene) && (
            <Block label="Hygiene Observation" icon={Droplets}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 text-xs">
                {visit.water_source     && <span><b className="text-brand-muted">Water:</b> {visit.water_source}</span>}
                {visit.toilet_facility  && <span><b className="text-brand-muted">Toilet:</b> {visit.toilet_facility}</span>}
                {visit.waste_disposal   && <span><b className="text-brand-muted">Waste:</b> {visit.waste_disposal}</span>}
                {visit.ventilation      && <span><b className="text-brand-muted">Ventilation:</b> {visit.ventilation}</span>}
                {visit.overall_hygiene  && <span className="capitalize"><b className="text-brand-muted">Overall:</b> {visit.overall_hygiene}</span>}
              </div>
            </Block>
          )}

          {visit.care_provided      && <Block label="Care Provided"      icon={Heart}>{visit.care_provided}</Block>}
          {visit.education_provided && <Block label="Health Education"  icon={BookOpen}>{visit.education_provided}</Block>}
          {visit.remarks            && <Block label="Remarks">{visit.remarks}</Block>}

          {visit.follow_up_required && visit.follow_up_date && (
            <Block label="Follow-up Scheduled" icon={ClipboardCheck}>{visit.follow_up_date}</Block>
          )}

          {/* Photos */}
          {hasPhotos && (
            <Block label="Photos" icon={FileImage}>
              <div className="flex flex-wrap gap-2">
                {visit.photo_paths.map((p) => (
                  <a key={p} href={fileViewUrl(p)} target="_blank" rel="noreferrer">
                    <img src={fileViewUrl(p)} alt="" className="h-24 w-24 rounded-md object-cover border border-brand-border hover:opacity-90 transition-opacity" />
                  </a>
                ))}
              </div>
            </Block>
          )}

          {/* Location */}
          {visit.latitude && visit.longitude && (
            <Block label="Location" icon={MapPin}>
              <a target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${visit.latitude},${visit.longitude}`} className="text-xs text-brand-primary inline-flex items-center gap-1 underline">
                {visit.latitude.toFixed?.(4) || visit.latitude}, {visit.longitude.toFixed?.(4) || visit.longitude}
              </a>
            </Block>
          )}

          {/* AI Field Report */}
          <Block label="AI Field Report" icon={Sparkles}>
            <AiReportPanel
              contextType={isOriginal ? "patient" : "revisit"}
              entityId={isOriginal ? visit.id.replace(/^original-/, "") : visit.id}
              photoPaths={visit.photo_paths || []}
              initialText={visit.ai_report_text || ""}
              initialId={visit.ai_report_id || null}
            />
          </Block>

          {canDelete && (
            <button onClick={onDelete} className="text-xs text-brand-danger inline-flex items-center gap-1 hover:underline" data-testid={`delete-revisit-${visit.id}`}>
              <Trash2 className="h-3 w-3" /> Delete this revisit
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Block({ label, icon: Icon, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-brand-muted font-bold inline-flex items-center gap-1 mb-1.5">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function MiniStat({ label, value, accent = "#2B5F4E", small }) {
  return (
    <div className="rounded-md border border-brand-border p-2 bg-brand-surfaceAlt/60">
      <div className="text-[9px] uppercase tracking-wider text-brand-muted font-bold">{label}</div>
      <div className={`mt-0.5 font-heading font-semibold capitalize ${small ? "text-base" : "text-xl"} tabular-nums`} style={{ color: accent }}>{value}</div>
    </div>
  );
}
